/**
 * Page produit : galerie et sélecteur de taille.
 *
 * Tout fonctionne sans ce script. Le rail de vignettes est une navigation
 * par ancres, le sélecteur est un <details> natif, et le formulaire porte
 * déjà l'identifiant de la variante — un navigateur sans JS peut acheter.
 * Le script ne fait qu'éviter les rechargements de page.
 */

/* ── galerie : le rail est le sommaire, la pile est le document ───────── */

function pileDe(bouton) {
  return bouton.closest('.cz-gal')?.querySelector('[data-galerie-pile]') ?? null;
}

/** Une piste qui déborde en largeur est le slider mobile ; sinon c'est la pile
 *  verticale du desktop. On lit la mise en page plutôt que de recopier ici le
 *  point de rupture du CSS : deux valeurs à tenir finissent par diverger. */
function estHorizontale(piste) {
  return piste.scrollWidth > piste.clientWidth + 1;
}

document.addEventListener('click', (event) => {
  const bouton = event.target.closest('[data-galerie-vers]');
  if (!bouton) return;

  const pile = pileDe(bouton);
  const vue = pile?.querySelector(`[data-galerie-vue="${bouton.dataset.galerieVers}"]`);
  if (!vue) return;

  const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const glisse = estHorizontale(pile);
  vue.scrollIntoView({
    behavior: reduit ? 'auto' : 'smooth',
    // En slider, `start` sur l'axe vertical remonterait la page alors que
    // seule la piste doit bouger : on garde `nearest` et on vise l'horizontale.
    block: glisse ? 'nearest' : 'start',
    inline: glisse ? 'center' : 'nearest',
  });
});

/**
 * Le rail ne montre jamais ses dix vignettes d'un coup : 620px de vignettes
 * dans une bande de 326 sur mobile, et le desktop a le même angle mort en
 * hauteur. La vignette active est donc ramenée dans le champ.
 *
 * On agit sur le seul défilement du rail, jamais via `scrollIntoView` : celui-ci
 * remonterait aussi la page, en plein geste de défilement de l'utilisateur.
 *
 * Les positions se déduisent des rectangles plutôt que d'`offsetLeft` : ce
 * dernier se mesure depuis l'ancêtre positionné, et le rail est tantôt
 * `sticky`, tantôt `static` selon la largeur.
 */
function suivreLaVignette(rail, actif) {
  if (!actif) return;

  const cadre = rail.getBoundingClientRect();
  const cible = actif.getBoundingClientRect();
  const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const options = { behavior: reduit ? 'auto' : 'smooth' };

  if (estHorizontale(rail)) {
    options.left = rail.scrollLeft + (cible.left - cadre.left) - (cadre.width - cible.width) / 2;
  } else if (rail.scrollHeight > rail.clientHeight + 1) {
    options.top = rail.scrollTop + (cible.top - cadre.top) - (cadre.height - cible.height) / 2;
  } else {
    return;
  }

  rail.scrollTo(options);
}

/**
 * La vignette active suit la photo qu'on regarde. Un observateur plutôt
 * qu'un écouteur de défilement : le navigateur fait le calcul hors du fil
 * principal, et rien ne se déclenche quand la page ne bouge pas.
 */
function suivreLaGalerie(galerie) {
  const pile = galerie.querySelector('[data-galerie-pile]');
  const rail = galerie.querySelector('[data-galerie-rail]');
  if (!pile || !rail) return;

  const vues = [...pile.querySelectorAll('[data-galerie-vue]')];

  /**
   * La photo regardée est celle dont le centre est le plus proche du centre de
   * la vue, sur l'axe qui défile.
   *
   * On mesure au lieu de comparer des taux de recouvrement : la bande utile ne
   * couvre la photo que sur une soixantaine de pixels de haut, ce qui plaçait
   * tous les taux autour de 0,245 — à égalité à quelques millièmes près, et la
   * vignette active se décalait d'un cran dans le sens du geste.
   */
  function photoRegardee() {
    const cadre = pile.getBoundingClientRect();
    const glisse = estHorizontale(pile);
    const centre = glisse ? cadre.left + cadre.width / 2 : window.innerHeight / 2;

    let gagnante = null;
    let ecartMin = Infinity;
    for (const vue of vues) {
      const boite = vue.getBoundingClientRect();
      const milieu = glisse ? boite.left + boite.width / 2 : boite.top + boite.height / 2;
      const ecart = Math.abs(milieu - centre);
      if (ecart < ecartMin) {
        ecartMin = ecart;
        gagnante = vue;
      }
    }
    return gagnante;
  }

  function majActive() {
    const vue = photoRegardee();
    if (!vue) return;
    const rang = vue.dataset.galerieVue;
    let actif = null;
    rail.querySelectorAll('[data-galerie-vers]').forEach((bouton) => {
      const courant = bouton.dataset.galerieVers === rang;
      bouton.setAttribute('aria-current', String(courant));
      if (courant) actif = bouton;
    });
    suivreLaVignette(rail, actif);
  }

  /* L'observateur ne sert plus qu'à savoir que quelque chose a bougé sans
     écouter le défilement de la page : le calcul, lui, se fait à la mesure. */
  const observateur = new IntersectionObserver(majActive, {
    rootMargin: '-45% 0px -45% 0px',
    threshold: [0, 0.25, 0.5, 0.75, 1],
  });
  vues.forEach((vue) => observateur.observe(vue));

  /* En slider, la piste défile sans que la page bouge : l'observateur ne voit
     plus rien passer une fois la diapositive calée. On écoute donc la piste
     elle-même, au rythme de l'affichage — dix mesures, jamais pendant un
     recalcul de mise en page. */
  let planifie = false;
  pile.addEventListener(
    'scroll',
    () => {
      if (planifie) return;
      planifie = true;
      requestAnimationFrame(() => {
        planifie = false;
        majActive();
      });
    },
    { passive: true },
  );
}

/* ── sélecteur de taille ───────────────────────────────────────────────
   Chaque tuile porte déjà son prix mis en forme par Liquid et l'identifiant
   de sa variante. Sur un produit à une seule option — 68 des 73 du catalogue
   — le script n'a donc rien à chercher : il recopie.

   Au-delà d'une option, une valeur ne suffit plus à désigner une variante :
   on interroge alors le JSON que Shopify publie déjà pour le produit, plutôt
   que de tenir un second inventaire dans le markup. */

async function variantes(section) {
  if (section._variantes !== undefined) return section._variantes;
  try {
    const reponse = await fetch(`${section.dataset.produitUrl}.js`);
    section._variantes = reponse.ok ? await reponse.json() : null;
  } catch {
    // Réseau coupé : le formulaire garde la variante rendue par le serveur,
    // et un envoi de page classique reste possible.
    section._variantes = null;
  }
  return section._variantes;
}

function peindre(section, { id, prix, prixBarre, dispo }) {
  const champ = section.querySelector('[data-variante-id]');
  const affichePrix = section.querySelector('[data-prix-courant]');
  const afficheBarre = section.querySelector('[data-prix-barre]');
  /* Deux exemplaires du bouton d'achat : celui de la colonne, et celui de
     la barre collée en bas sur petit écran. Les deux doivent connaître la
     disponibilité de la variante — un bouton actif sur une taille épuisée
     mène droit à une erreur de panier. */
  const boutons = section.querySelectorAll('[data-panier]');
  const textes = section.querySelectorAll('[data-panier-texte]');

  if (champ && id) champ.value = id;
  if (affichePrix && prix) affichePrix.textContent = prix;
  if (afficheBarre) {
    afficheBarre.hidden = !prixBarre;
    if (prixBarre) afficheBarre.textContent = prixBarre;
  }
  boutons.forEach((b) => {
    b.disabled = !dispo;
  });
  textes.forEach((t) => {
    t.textContent = dispo ? section.dataset.texteAjout : section.dataset.texteEpuise;
  });

  // L'URL suit la taille choisie : un lien copié rouvre la bonne variante.
  if (id) {
    const adresse = new URL(window.location.href);
    adresse.searchParams.set('variant', id);
    window.history.replaceState({}, '', adresse);
  }
}

function choixCourants(section) {
  return [...section.querySelectorAll('[data-option-position]')].map(
    (etape) => etape.querySelector('.step-tile.on')?.dataset.optionChoix ?? null,
  );
}

document.addEventListener('click', async (event) => {
  const tuile = event.target.closest('.step-tile');
  if (!tuile || tuile.disabled) return;

  const etape = tuile.closest('[data-option-position]');
  const section = tuile.closest('.pdp');
  if (!etape || !section) return;

  etape.querySelectorAll('.step-tile').forEach((autre) => {
    const actif = autre === tuile;
    autre.classList.toggle('on', actif);
    autre.setAttribute('aria-checked', String(actif));
  });
  const resume = etape.querySelector('[data-option-valeur]');
  if (resume) resume.textContent = tuile.dataset.optionChoix;
  etape.open = false;

  if (Number(section.dataset.options) === 1) {
    peindre(section, {
      id: tuile.dataset.variante,
      prix: tuile.dataset.prix,
      prixBarre: tuile.dataset.prixBarre,
      dispo: tuile.dataset.dispo === 'true',
    });
    return;
  }

  const donnees = await variantes(section);
  if (!donnees) return;
  const choix = choixCourants(section);
  const v = donnees.variants.find((candidate) =>
    choix.every((valeur, rang) => valeur === null || candidate.options[rang] === valeur),
  );
  if (!v) return;

  // Le prix vient de la tuile de la MÊME variante : elle porte la mise en
  // forme de Liquid, que le JSON de l'API ne fournit pas.
  const jumelle = section.querySelector(`.step-tile[data-variante="${v.id}"]`);
  peindre(section, {
    id: v.id,
    prix: jumelle?.dataset.prix,
    prixBarre: jumelle?.dataset.prixBarre,
    dispo: v.available,
  });
});

document.querySelectorAll('.cz-gal').forEach(suivreLaGalerie);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('.cz-gal').forEach(suivreLaGalerie);
});

/* ── quand je le reçois ────────────────────────────────────────────────
   Une fenêtre datée plutôt qu'un délai : « ven. 28 – sam. 29 » se projette,
   « sous 48 h » demande un calcul au lecteur.

   Le calcul est fait ici et non dans Liquid : Shopify met les pages en
   cache, une date rendue par le serveur se figerait au jour de la mise en
   cache. Le HTML garde le délai en toutes lettres — c'est lui que voit un
   visiteur sans script, et ce que lit un moteur de recherche. */
function daterLaLivraison(champ) {
  const premier = Number(champ.dataset.fenetreLivraison);
  if (!Number.isFinite(premier) || premier < 1) return;

  const format = new Intl.DateTimeFormat(document.documentElement.lang || 'fr', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const jour = (dans) => {
    const d = new Date();
    d.setDate(d.getDate() + dans);
    return format.format(d);
  };
  champ.textContent = `${jour(premier)} – ${jour(premier + 1)}`;
}

document.querySelectorAll('[data-fenetre-livraison]').forEach(daterLaLivraison);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-fenetre-livraison]').forEach(daterLaLivraison);
});

/* ── photo en grand ────────────────────────────────────────────────────
   Un <dialog> natif : Échap, le piège à focus et le verrou de défilement
   viennent du navigateur.

   Les sources sont lues dans la galerie elle-même — on affiche la plus
   grande taille que le srcset propose, sans second inventaire à tenir. */
function photosDe(section) {
  return [...section.querySelectorAll('[data-galerie-vue] img')].map((img) => ({
    src: img.currentSrc || img.src,
    alt: img.alt,
    largeur: img.naturalWidth || img.width,
    hauteur: img.naturalHeight || img.height,
  }));
}

function montrer(dialogue, photos, rang) {
  const total = photos.length;
  // La galerie boucle : après la dernière photo vient la première.
  const i = ((rang % total) + total) % total;
  dialogue._rang = i;
  const img = dialogue.querySelector('[data-lightbox-img]');
  img.src = photos[i].src;
  img.alt = photos[i].alt;
  // Les dimensions suivent la photo : sans elles la mise en page saute
  // entre deux formats.
  if (photos[i].largeur) img.width = photos[i].largeur;
  if (photos[i].hauteur) img.height = photos[i].hauteur;
}

document.addEventListener('click', (event) => {
  const loupe = event.target.closest('[data-zoom]');
  if (loupe) {
    // La boîte de dialogue vit hors de `.pdp` — un élément en position fixe
    // placé dans un parent sticky se cale sur ce parent, pas sur la fenêtre.
    // On remonte donc jusqu'au wrapper de section pour la trouver.
    const enveloppe = loupe.closest('.shopify-section') ?? document;
    const dialogue = enveloppe.querySelector('[data-lightbox]');
    const galerie = enveloppe.querySelector('.cz-gal');
    if (!dialogue || !galerie) return;
    dialogue._photos = photosDe(galerie);
    montrer(dialogue, dialogue._photos, Number(loupe.dataset.zoom));
    dialogue.showModal();
    return;
  }

  const dialogue = event.target.closest('[data-lightbox]');
  if (!dialogue) return;

  if (event.target.closest('[data-lightbox-close]')) dialogue.close();
  else if (event.target.closest('[data-lightbox-prev]')) montrer(dialogue, dialogue._photos, dialogue._rang - 1);
  else if (event.target.closest('[data-lightbox-next]')) montrer(dialogue, dialogue._photos, dialogue._rang + 1);
});

/* Les flèches du clavier naviguent, comme dans n'importe quelle visionneuse. */
document.addEventListener('keydown', (event) => {
  const dialogue = document.querySelector('[data-lightbox][open]');
  if (!dialogue?._photos) return;
  if (event.key === 'ArrowLeft') montrer(dialogue, dialogue._photos, dialogue._rang - 1);
  else if (event.key === 'ArrowRight') montrer(dialogue, dialogue._photos, dialogue._rang + 1);
});

/* ── guide des tailles ─────────────────────────────────────────────────
   Un <dialog> natif de plus : Échap, le piège à focus, le voile et le
   verrou de défilement viennent du navigateur.

   Cliquer une ligne du tableau choisit la taille : c'est le geste attendu
   quand on vient d'y lire un prix. */
document.addEventListener('click', (event) => {
  if (event.target.closest('[data-guide-ouvrir]')) {
    event.target.closest('.pdp')?.parentElement?.querySelector('[data-guide]')?.showModal();
    return;
  }
  if (event.target.closest('[data-guide-fermer]')) {
    event.target.closest('[data-guide]')?.close();
    return;
  }

  const ligne = event.target.closest('[data-guide-taille]');
  if (!ligne) return;
  const guide = ligne.closest('[data-guide]');
  const section = guide?.parentElement?.querySelector('.pdp');
  const tuile = section?.querySelector(`.step-tile[data-variante="${ligne.dataset.guideTaille}"]`);
  if (!tuile) return;
  /* La taille s'applique à la fiche, mais le guide reste ouvert — comme le
     prototype : c'est là qu'on lit le prix et qu'on découvre les
     compléments de cette dimension. La croix ou le voile ferment. */
  tuile.click();
  /* On marque par dimension et non par élément : sur téléphone la même
     taille existe deux fois, en pastille et en carte. Comparer les
     éléments laissait l'une des deux marquée et l'autre non. */
  guide
    .querySelectorAll('[data-guide-taille]')
    .forEach((l) => l.classList.toggle('on', l.dataset.guideTaille === ligne.dataset.guideTaille));
  suivreLaDimension(guide, tuile.dataset.optionChoix ?? ligne.textContent.trim());
});

/**
 * Clic sur le voile : le <dialog> reçoit l'événement sur toute la fenêtre,
 * même s'il ne peint qu'un panneau. On compare la position du clic à la
 * boîte réelle du panneau — la recette du tiroir de menu (header.js).
 */
document.addEventListener('click', (event) => {
  const guide = event.target.closest('[data-guide]');
  if (!guide || event.target !== guide) return;
  const boite = guide.getBoundingClientRect();
  const dedans =
    event.clientX >= boite.left &&
    event.clientX <= boite.right &&
    event.clientY >= boite.top &&
    event.clientY <= boite.bottom;
  if (!dedans) guide.close();
});

/**
 * Les compléments du guide suivent la dimension choisie.
 *
 * Chaque carte emporte ses variantes en JSON. On retient celle dont le
 * titre porte les mêmes chiffres que la taille cliquée — « 90x190 »,
 * « 90 × 190 cm » et « 90x190 cm » désignent la même chose, et les
 * catalogues n'écrivent jamais deux fois pareil. Sans correspondance, un
 * lot d'oreillers par exemple, la carte garde son prix « dès » et sa
 * première variante, comme dans le prototype.
 */
function chiffres(texte) {
  return String(texte ?? '').replace(/\D+/g, '');
}

function suivreLaDimension(guide, taille) {
  const zone = guide.querySelector('[data-guide-xs]');
  if (!zone) return;

  const etiquette = zone.querySelector('[data-guide-xs-taille]');
  if (etiquette) etiquette.textContent = taille;

  const cle = chiffres(taille);
  zone.querySelectorAll('[data-guide-xs-carte]').forEach((carte) => {
    let variantes;
    try {
      variantes = JSON.parse(carte.querySelector('[data-guide-xs-variantes]')?.textContent ?? '{}');
    } catch {
      return;
    }
    const prix = carte.querySelector('[data-guide-xs-prix]');
    const champ = carte.querySelector('[data-guide-xs-id]');
    const bouton = carte.querySelector('[data-guide-xs-ajout]');

    const titre = cle && Object.keys(variantes).find((t) => chiffres(t) === cle);
    if (!titre) {
      if (prix) prix.textContent = (prix.dataset.varie === 'true' ? 'dès ' : '') + prix.dataset.prixDes;
      if (bouton) bouton.disabled = false;
      return;
    }
    const v = variantes[titre];
    if (prix) prix.textContent = v.prix;
    if (champ) champ.value = v.id;
    if (bouton) bouton.disabled = !v.dispo;
  });
}

/* À l'ouverture, les compléments se calent sur la taille déjà choisie. */
document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-guide-ouvrir]')) return;
  const guide = event.target.closest('.pdp')?.parentElement?.querySelector('[data-guide]');
  const ligne = guide?.querySelector('tr[data-guide-taille].on');
  if (guide && ligne) suivreLaDimension(guide, ligne.querySelector('th')?.textContent.trim() ?? '');
});

/* ── compléments : deux par page, flèches ──────────────────────────────
   Le défilement est natif (scroll-snap) : le script ne fait que pousser la
   piste d'une page et griser les flèches en bout de course. */
document.addEventListener('click', (event) => {
  const bouton = event.target.closest('[data-xs-prev], [data-xs-next]');
  if (!bouton) return;
  const piste = bouton.closest('.cz-xs')?.querySelector('[data-xs-track]');
  if (!piste) return;
  const sens = bouton.hasAttribute('data-xs-prev') ? -1 : 1;
  piste.scrollBy({ left: sens * piste.clientWidth });
});

function majFleches(piste) {
  const zone = piste.closest('.cz-xs');
  const prev = zone?.querySelector('[data-xs-prev]');
  const next = zone?.querySelector('[data-xs-next]');
  // Tolérance de 2px : les positions fractionnaires des écrans à densité
  // élevée laissaient sinon un bouton actif à l'arrêt.
  if (prev) prev.disabled = piste.scrollLeft <= 2;
  if (next) next.disabled = piste.scrollLeft >= piste.scrollWidth - piste.clientWidth - 2;
}

document.addEventListener(
  'scroll',
  (event) => {
    const piste = event.target;
    if (piste instanceof Element && piste.matches?.('[data-xs-track]')) majFleches(piste);
  },
  true,
);

const rafraichirComplements = () => document.querySelectorAll('[data-xs-track]').forEach(majFleches);
window.addEventListener('resize', rafraichirComplements);
document.addEventListener('shopify:section:load', rafraichirComplements);
rafraichirComplements();
